import { useParams } from 'react-router-dom';
import type { ReactNode } from 'react';

import Grid from '@mui/material/Grid';

import { buildPath } from '@e-socna/utils';
import type { FragmentType } from '@e-socna/data-layer';

import {
  PATH_MISSION_SERVICE_USAGES,
  PATH_MISSION_SERVICE_USAGES_SUB_MISSION,
} from '../../../app/constants/paths.constant';
import { FILTERABLE_CARD_DEFAULT_SIZE } from '../../../components/FilterableCards/constants/card.constant';
import type { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from '../../../components/ServiceUsages/TotalCard';

import { SubMissionsSection } from './SubMissionsSection';
import { SubMissionPricingCard } from './SubMissionPricingCard';
import { SubMissionPricingDrawer } from './SubMissionPricingDrawer';
import type { SubMissionPricing } from '../types/sub-mission-pricing.type';

export interface SubMissionPricingCardItem {
  readonly subMission: SubMissionPricing;
  readonly totalCalculation: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
  // The pricing detail of the sub-mission, shown in a drawer.
  readonly detail: ReactNode;
}

// The route patterns of the pricing page and of a sub-mission detail on it.
export interface SubMissionPricingRoutes {
  readonly page: string;
  readonly detail: string;
}

const CONSULTATION_ROUTES: SubMissionPricingRoutes = {
  page: PATH_MISSION_SERVICE_USAGES,
  detail: PATH_MISSION_SERVICE_USAGES_SUB_MISSION,
};

interface Props {
  readonly consultInNewTab?: boolean;
  readonly items: readonly SubMissionPricingCardItem[];
  readonly routes?: SubMissionPricingRoutes;
}

export const SubMissionPricingCards = ({
  consultInNewTab = false,
  items,
  routes = CONSULTATION_ROUTES,
}: Readonly<Props>) => {
  const params = useParams();

  return (
    <SubMissionsSection>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {items.map(({ subMission, totalCalculation }) => (
          <Grid
            key={subMission._id}
            size={FILTERABLE_CARD_DEFAULT_SIZE}
            sx={{ display: 'flex' }}
          >
            <SubMissionPricingCard
              consultInNewTab={consultInNewTab}
              detailPath={buildPath(routes.detail, {
                ...params,
                subMissionId: subMission._id,
              })}
              subMission={subMission}
              totalCalculation={totalCalculation}
            />
          </Grid>
        ))}
      </Grid>
      <SubMissionPricingDrawer items={items} pagePath={routes.page} />
    </SubMissionsSection>
  );
};
